const walletRejectionPhrases = [
    "user rejected the request",
    "user rejected action",
    "user denied transaction signature",
    "rejectallapprovals",
] as const;

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null;

const getRecord = (
    record: Record<string, unknown>,
    key: string,
): Record<string, unknown> | undefined => {
    const value = record[key];
    return isRecord(value) ? value : undefined;
};

const hasWalletRejectionCode = (value: unknown): boolean =>
    value === "ACTION_REJECTED" || value === 4001 || value === "4001";

const hasWalletRejectionPhrase = (value: unknown): boolean =>
    typeof value === "string" &&
    walletRejectionPhrases.some((phrase) =>
        value.toLowerCase().includes(phrase),
    );

const getWalletRejectionFields = (
    message: Record<string, unknown>,
): {
    codes: unknown[];
    phrases: unknown[];
} => {
    const error = getRecord(message, "error");
    const info = getRecord(message, "info");
    const infoError = info ? getRecord(info, "error") : undefined;
    const data = getRecord(message, "data");
    const infoErrorData = infoError ? getRecord(infoError, "data") : undefined;

    return {
        codes: [message.code, error?.code, infoError?.code],
        phrases: [
            message.message,
            error?.message,
            infoError?.message,
            data?.cause,
            infoErrorData?.cause,
        ],
    };
};

export const isWalletRejectionError = (message: unknown): boolean => {
    if (hasWalletRejectionPhrase(message)) {
        return true;
    }

    if (!isRecord(message)) {
        return false;
    }

    const { codes, phrases } = getWalletRejectionFields(message);

    return (
        codes.some((code) => hasWalletRejectionCode(code)) ||
        phrases.some((phrase) => hasWalletRejectionPhrase(phrase))
    );
};

let walletRejectionMessage = "Wallet request rejected";

export const setWalletRejectionMessage = (message: string): void => {
    walletRejectionMessage = message;
};

export const toError = (value: unknown): Error =>
    value instanceof Error ? value : new Error(formatError(value));

export enum LnurlAmountErrorKind {
    Min = "min",
    Max = "max",
}

export class LnurlAmountError extends Error {
    public readonly kind: LnurlAmountErrorKind;
    public readonly limitMsat: number;
    public readonly limitSat: number;

    constructor(kind: LnurlAmountErrorKind, limitMsat: number) {
        super(kind === LnurlAmountErrorKind.Min ? "minAmount" : "maxAmount", {
            cause: limitMsat,
        });
        this.name = "LnurlAmountError";
        this.kind = kind;
        this.limitMsat = limitMsat;
        // Rounded into the satisfiable range: up for a minimum, down for a maximum.
        this.limitSat =
            kind === LnurlAmountErrorKind.Min
                ? Math.ceil(limitMsat / 1_000)
                : Math.floor(limitMsat / 1_000);
    }
}

export const isLnurlAmountError = (value: unknown): value is LnurlAmountError =>
    value instanceof LnurlAmountError;

export class BridgeCapacityError extends Error {
    // available/requested are in the bridge token's native decimals
    constructor(
        public readonly available: bigint,
        public readonly requested: bigint,
        options?: ErrorOptions,
    ) {
        super(
            `bridge capacity exceeded: requested ${requested}, available ${available}`,
            options,
        );
        this.name = "BridgeCapacityError";
    }
}

export const isBridgeCapacityError = (
    value: unknown,
): value is BridgeCapacityError => value instanceof BridgeCapacityError;

export const formatError = (message: unknown): string => {
    if (isWalletRejectionError(message)) {
        return walletRejectionMessage;
    }

    if (typeof message === "string") {
        return message;
    }

    if (isRecord(message)) {
        const msgObj = message;

        if (isRecord(msgObj.error)) {
            const err = msgObj.error;

            if (typeof err.message === "string") {
                return err.message;
            }
        }

        if (typeof msgObj.message === "string") {
            return msgObj.message;
        }

        if (typeof msgObj.error === "string") {
            return msgObj.error;
        }

        if (typeof msgObj.data === "string") {
            return msgObj.data;
        }

        if (
            typeof message.toString === "function" &&
            // eslint-disable-next-line @typescript-eslint/no-base-to-string
            message.toString() !== "[object Object]"
        ) {
            // eslint-disable-next-line @typescript-eslint/no-base-to-string
            return message.toString();
        }
    }

    return JSON.stringify(message);
};
